import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileDown, Loader2, Mail } from 'lucide-react';
import { FirmaWebSign } from './FirmaWebSign';
import { RutField, fillEmpty } from '../RutField';
import { NotaryProfileForm, useNotaryProfile } from './NotaryProfileForm';

const input = 'w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500';

const ClientPick = ({ clients, testid, onPick }) => (
  <select data-testid={testid} defaultValue="" onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); if (c) onPick(c); }} className={`${input} text-xs`}>
    <option value="">Tomar datos de un cliente del CRM…</option>
    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.rut ? ` · ${c.rut}` : ''}</option>)}
  </select>
);

const Field = ({ v, k, set, ph, testid }) => (
  <input data-testid={testid} value={v[k] || ''} onChange={(e) => set({ ...v, [k]: e.target.value })} placeholder={ph} className={input} />
);

// RUT of a party: formatted, verified and, when found, fills that party's empty name field
const Rut = ({ v, k, set, ph, testid, nameKey }) => (
  <RutField testid={testid} value={v[k] || ''} onChange={(x) => set((p) => ({ ...p, [k]: x }))} onLookup={(d) => set((p) => fillEmpty(p, { [nameKey]: d.name }))} placeholder={ph} className={input} />
);

export const FiniquitoForm = ({ payload }) => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [employer, setEmployer] = useState({});
  const [worker, setWorker] = useState({});
  const [city, setCity] = useState('');
  const [repertorio, setRepertorio] = useState('');
  const [profile, setProfile, loaded] = useNotaryProfile();
  const [showNotary, setShowNotary] = useState(false);
  const [mailTo, setMailTo] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/crm/clients', { params: { limit: 100 } }).then((r) => setClients(r.data.clients || [])).catch(() => {}); }, [api]);

  const fromClient = (c) => ({
    name: c.company_name || c.name, rut: c.company_rut || c.rut, address: [c.address, c.city].filter(Boolean).join(', '),
    rep_name: c.contact_person || (c.company_name ? c.name : ''), rep_rut: c.company_name ? c.rut : '',
  });

  const body = { calc: payload, employer, worker, city, repertorio: repertorio || null };
  const sendMail = async () => {
    setBusy('mail');
    try {
      const r = await api.post('/labor/finiquito/email', { ...body, client_id: mailTo });
      toast.success(`Finiquito enviado a ${r.data.sent_to} (${r.data.email})`);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar el correo'); } finally { setBusy(false); }
  };
  const generate = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await api.post(`/labor/finiquito/${fmt}`, body, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `finiquito-${(worker.name || 'trabajador').replace(/[^\w\-áéíóúñÁÉÍÓÚÑ ]/g, '').replace(/ /g, '-')}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(fmt === 'docx' ? 'Finiquito y ratificación generados en Word' : 'Finiquito y ratificación generados en PDF');
    } catch { toast.error('No se pudo generar el finiquito'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="finiquito-form" className="bg-card border border-border rounded-xl p-6 space-y-4">
      <div>
        <h4 className="font-semibold text-white">Finiquito listo para firmar</h4>
        <p className="text-xs text-slate-400">Incluye la hoja de ratificación notarial con N° de repertorio y leyenda de firma electrónica avanzada (Ley 19.799).</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Empleador</p>
          <ClientPick clients={clients} testid="finiquito-employer-client" onPick={(c) => setEmployer({ ...employer, ...fromClient(c) })} />
          <Field v={employer} k="name" set={setEmployer} ph="Razón social o nombre" testid="finiquito-employer-name" />
          <Rut v={employer} k="rut" set={setEmployer} ph="RUT empleador" testid="finiquito-employer-rut" nameKey="name" />
          <Field v={employer} k="rep_name" set={setEmployer} ph="Representante legal" testid="finiquito-employer-rep" />
          <Rut v={employer} k="rep_rut" set={setEmployer} ph="RUT representante" testid="finiquito-employer-rep-rut" nameKey="rep_name" />
          <Field v={employer} k="address" set={setEmployer} ph="Domicilio" testid="finiquito-employer-address" />
          <Field v={employer} k="payment_method" set={setEmployer} ph="Forma de pago (ej. vale vista N° …)" testid="finiquito-payment" />
        </div>
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Trabajador</p>
          <ClientPick clients={clients} testid="finiquito-worker-client" onPick={(c) => setWorker({ ...worker, name: c.name, rut: c.rut, address: [c.address, c.city].filter(Boolean).join(', ') })} />
          <Field v={worker} k="name" set={setWorker} ph="Nombre completo" testid="finiquito-worker-name" />
          <Rut v={worker} k="rut" set={setWorker} ph="RUT trabajador" testid="finiquito-worker-rut" nameKey="name" />
          <Field v={worker} k="position" set={setWorker} ph="Cargo" testid="finiquito-worker-position" />
          <Field v={worker} k="address" set={setWorker} ph="Domicilio" testid="finiquito-worker-address" />
          <input data-testid="finiquito-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ciudad de firma" className={input} />
          <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold pt-2">Ratificación notarial</p>
          <input data-testid="finiquito-repertorio" value={repertorio} onChange={(e) => setRepertorio(e.target.value)} placeholder="N° de repertorio (ej. 1.245-2026)" maxLength={40} className={input} />
        </div>
      </div>
      <div data-testid="finiquito-notary-summary" className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-xs text-slate-300">
        <div className="flex items-center justify-between gap-2">
          <span>Notaría: {profile.notary_name ? `${profile.notary_name}, Notario Público de ${profile.comuna || '[COMUNA]'}` : 'sin datos guardados (quedará el espacio para completar)'}</span>
          <button data-testid="finiquito-notary-toggle" onClick={() => setShowNotary(!showNotary)} className="text-amber-400 underline shrink-0">{showNotary ? 'Cerrar' : 'Editar'}</button>
        </div>
        {showNotary && <div className="mt-3"><NotaryProfileForm profile={profile} setProfile={setProfile} loaded={loaded} compact /></div>}
      </div>
      <p className="text-xs text-slate-500">Los campos vacíos quedan marcados entre corchetes (ej. [RUT], [DOMICILIO]) para completarlos en Word.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <button data-testid="finiquito-generate-btn" onClick={() => generate('docx')} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy === 'docx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}Word editable
        </button>
        <button data-testid="finiquito-pdf-btn" onClick={() => generate('pdf')} disabled={!!busy} className="bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}PDF
        </button>
      </div>
      <div data-testid="finiquito-email" className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-2">
        <select data-testid="finiquito-email-client" value={mailTo} onChange={(e) => setMailTo(e.target.value)} className={`${input} text-xs`}>
          <option value="">Enviar por correo a un cliente del CRM…</option>
          {clients.filter((c) => c.email).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email}</option>)}
        </select>
        <button data-testid="finiquito-email-btn" onClick={sendMail} disabled={!mailTo || !!busy} className="bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg px-4 py-2 flex items-center justify-center gap-2 disabled:opacity-40">
          {busy === 'mail' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Enviar
        </button>
      </div>
      <FirmaWebSign body={body} defaultSigner={profile.signer} />
    </div>
  );
};
